import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  LayoutDashboard,
  Loader2,
  Mail,
  Plus,
  Sparkles,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useAuth } from '../hooks/useAuth';
import { useCommandPalette } from '../hooks/useCommandPalette';
import { useSidePanel } from '../contexts/SidePanelContext';
import { patientsService } from '../services/patientsService';
import type { Patient } from '../services/patientsService';
import { fullName } from './patients/patientPresentation';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';

interface Destination {
  id: string;
  label: string;
  icon: LucideIcon;
  run: () => void;
  adminOnly?: boolean;
  keywords?: string;
}

const SEARCH_DEBOUNCE_MS = 200;

/**
 * ⌘K from anywhere: patients by name, the routes, and the few actions worth
 * reaching without a mouse.
 *
 * A clinician opens a patient dozens of times a day. Before this the path was
 * sidebar → patients → search box → type → click the row; now it is one
 * keystroke and the name.
 *
 * The patient search hits the same endpoint the list uses, debounced, so the
 * palette never holds a stale copy of the roster.
 */
const CommandPalette = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const sidePanel = useSidePanel();
  const palette = useCommandPalette();
  const open = palette?.open ?? false;
  const setOpen = palette?.setOpen ?? (() => {});
  const [term, setTerm] = useState('');
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searching, setSearching] = useState(false);
  const seq = useRef(0);

  // Each open starts clean: the previous search is rarely the next one.
  useEffect(() => {
    if (!open) {
      setTerm('');
      setPatients([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = term.trim();
    if (query.length < 2) {
      setPatients([]);
      setSearching(false);
      return;
    }

    const current = ++seq.current;
    setSearching(true);
    const timer = setTimeout(() => {
      patientsService
        .getPatients({ page: 1, search: query, ordering: 'name', perPage: 8 })
        .then((response) => {
          // A slower earlier request must not overwrite a newer one.
          if (seq.current === current) setPatients(response.results ?? []);
        })
        .catch((error) => console.error('CommandPalette: patient search failed', error))
        .finally(() => {
          if (seq.current === current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, open]);

  const go = (action: () => void) => {
    setOpen(false);
    action();
  };

  const destinations: Destination[] = useMemo(
    () => [
      {
        id: 'dashboard',
        label: 'Inicio',
        icon: LayoutDashboard,
        keywords: 'dashboard panel',
        run: () => navigate('/app'),
      },
      {
        id: 'patients',
        label: 'Pacientes',
        icon: Users,
        keywords: 'lista',
        run: () => navigate('/app/patients'),
      },
      {
        id: 'attention',
        label: 'Pacientes que necesitan atención',
        icon: Activity,
        keywords: 'urgente cola',
        run: () => navigate('/app/patients?view=attention'),
      },
      {
        id: 'resources',
        label: 'Recursos',
        icon: BookOpen,
        keywords: 'catálogo suplementos medicamentos ingredientes actividades',
        run: () => navigate('/app/resources'),
      },
      {
        id: 'clinics',
        label: 'Clínicas',
        icon: Building2,
        keywords: 'médicos nutricionistas centros',
        adminOnly: true,
        run: () => navigate('/app/clinics'),
      },
      {
        id: 'metrics',
        label: 'Métricas',
        icon: BarChart3,
        adminOnly: true,
        run: () => navigate('/app/metrics'),
      },
      {
        id: 'invites',
        label: 'Invitaciones',
        icon: Mail,
        adminOnly: true,
        run: () => navigate('/app/invites'),
      },
    ],
    [navigate],
  );

  const visibleDestinations = destinations.filter(
    (d) => !d.adminOnly || user?.role === 'admin',
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Buscar"
      description="Busca un paciente o salta a una sección"
    >
      <CommandInput
        placeholder="Buscar pacientes, secciones o acciones…"
        value={term}
        onValueChange={setTerm}
      />
      <CommandList>
        {searching && (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Buscando…
          </div>
        )}

        {!searching && <CommandEmpty>Nada coincide con «{term}».</CommandEmpty>}

        {patients.length > 0 && (
          <>
            <CommandGroup heading="Pacientes">
              {patients.map((patient) => (
                <CommandItem
                  key={patient.id}
                  // cmdk filters on this string, and the backend matches on
                  // name, email and phone — so all three go in, or a patient
                  // the server found by email would be filtered back out here.
                  value={`${fullName(patient)} ${patient.email ?? ''} ${patient.phone_number ?? ''}`}
                  onSelect={() => go(() => navigate(`/app/patients/${patient.id}`))}
                >
                  <Users />
                  <span className="flex-1">{fullName(patient)}</span>
                  <span className="text-xs text-muted-foreground">{patient.email}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Ir a">
          {visibleDestinations.map((destination) => (
            <CommandItem
              key={destination.id}
              value={`${destination.label} ${destination.keywords ?? ''}`}
              onSelect={() => go(destination.run)}
            >
              <destination.icon />
              {destination.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Acciones">
          <CommandItem
            value="nuevo paciente crear alta"
            onSelect={() => go(() => navigate('/app/patients?create=true'))}
          >
            <Plus />
            Nuevo paciente
          </CommandItem>
          <CommandItem
            value="sissi ai asistente agente ia chat"
            onSelect={() => go(() => sidePanel?.openAgent())}
          >
            <Sparkles />
            Abrir Sissi AI
            <CommandShortcut>IA</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
